import { useEffect } from 'react';
import { X, ShieldAlert } from 'lucide-react';

interface BlockUserDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  isBlocking: boolean;
  username: string;
}

export function BlockUserDialog({
  isOpen,
  onClose,
  onConfirm,
  isBlocking,
  username,
}: BlockUserDialogProps) {
  // Close on Escape key
  useEffect(() => {
    if (!isOpen) return;
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape' && !isBlocking) {
        onClose();
      }
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, isBlocking, onClose]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm"
      onClick={() => { if (!isBlocking) onClose(); }}
    >
      <div
        className="bg-[#1A1F26] rounded-xl w-full max-w-md"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-white/[0.06]">
          <div className="flex items-center gap-3">
            <ShieldAlert className="text-red-400" size={24} />
            <h2 className="text-xl font-semibold text-white">Block @{username}?</h2>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-white transition-colors"
            disabled={isBlocking}
          >
            <X size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          <p className="text-slate-300 mb-4">
            Blocking this user will:
          </p>
          <ul className="text-slate-400 text-sm space-y-2 mb-6 list-disc list-inside">
            <li>Remove them from your followers and following</li>
            <li>Hide their activity from your feed</li>
            <li>Prevent them from seeing your profile</li>
            <li>Remove any pending recommendations between you</li>
            <li>Hide them from your search results</li>
          </ul>

          <div className="flex gap-3">
            <button
              onClick={onClose}
              disabled={isBlocking}
              className="flex-1 px-4 py-2 bg-[#2A2F36] hover:bg-[#3A3F46] text-white rounded-lg transition-colors font-medium disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={onConfirm}
              disabled={isBlocking}
              className="flex-1 px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors font-medium disabled:opacity-50"
            >
              {isBlocking ? 'Blocking...' : 'Block'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
